import classNames from "classnames"
import React from "react"

import { extractFromTag, getThemeColor, matchCellPatterns } from "./utils"
import GatsbyStoryblokImage from "../../../elements/V2/GatsbyStoryblokImage"
import Typography from "../../../elements/V2/Typography"

import Logo from "@components/elements/V2/Icons/YocoLogoIcon"
import { TableRegex } from "@constants/tables"

type HeaderDropdownProps = {
  availableColumns: number[]
  data: string[][]
  columnImages?: string[]
  displayIndex: number
  selectedColumnsLength: number
  theme: "light" | "dark"
  onSelectColumn: (columnIndex: number) => void
}

const HeaderDropdown = ({
  availableColumns,
  data,
  columnImages,
  displayIndex,
  selectedColumnsLength,
  theme,
  onSelectColumn,
}: HeaderDropdownProps) => {
  return (
    <div
      className={classNames(
        "absolute z-20 -mt-0.5 flex w-1/2 flex-col rounded-b-10 !border-t-0 shadow-lg sm:w-1/3 sm:border",
        theme === "light"
          ? "border-charcoal/10 bg-white"
          : "border-charcoal bg-black",
        {
          "left-0": displayIndex === 0,
          "right-0": displayIndex === selectedColumnsLength - 1,
          "left-1/2 sm:-translate-x-1/2":
            displayIndex > 0 && displayIndex < selectedColumnsLength - 1,
          "!w-1/2": selectedColumnsLength === 2,
        }
      )}
    >
      {availableColumns.map((availableColumnIndex, index) => {
        const item = data[0][availableColumnIndex]
        const matches = matchCellPatterns(item)
        return (
          <button
            key={item}
            onClick={() => {
              onSelectColumn(availableColumnIndex)
            }}
            className="flex flex-col"
          >
            {index === 0 && (
              <hr
                className={classNames(
                  "hidden w-full sm:block",
                  theme === "light" ? "border-charcoal/10" : "border-charcoal"
                )}
              />
            )}
            <div
              className={classNames(
                "flex h-[72px] cursor-pointer items-center gap-16 px-8"
              )}
            >
              {columnImages && columnImages[availableColumnIndex] && (
                <div className="ml-8 aspect-square h-32 w-32 sm:h-64 sm:w-64">
                  <GatsbyStoryblokImage
                    aspectRatio="fullWidth"
                    image={extractFromTag(
                      columnImages[availableColumnIndex],
                      "image"
                    )}
                    alt={
                      matches.isProduct
                        ? item.match(TableRegex.PRODUCT)![1]
                        : ""
                    }
                    className="h-32 w-32 sm:h-64 sm:w-64 md:h-full md:w-auto"
                    testId="comparison-table-image"
                    imageObjectFit="contain"
                  />
                </div>
              )}
              {matches.isYocoLogo ? (
                <Logo
                  className={classNames(
                    "max-h-32",
                    theme === "light" ? "fill-black" : "fill-white"
                  )}
                  alt="Yoco Logo"
                />
              ) : (
                <Typography
                  font="grotesk"
                  weight="medium"
                  color={getThemeColor(theme)}
                  size="body-lg"
                  text={
                    matches.isProduct
                      ? matches.isProduct[1]
                      : extractFromTag(item, "title")
                  }
                  className="text-start"
                />
              )}
            </div>
            {index !== availableColumns.length - 1 && (
              <hr
                className={classNames(
                  "w-full",
                  theme === "light" ? "border-charcoal/10" : "border-charcoal"
                )}
              />
            )}
          </button>
        )
      })}
    </div>
  )
}

export default HeaderDropdown
